import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme, useMediaQuery } from "@mui/material";
import { Modal } from "@mui/material";
import Chip from "@mui/material/Chip";
import Fab from "@mui/material/Fab";
import Grid from "@mui/material/Grid";
import Input from "@mui/material/Input";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { useDispatch } from "react-redux";
import { request } from "@/Api";
import BidRowButtons from "@/bids/BidRowButtons";
import { InternalLink } from "@/common/CommonComponents";
import { setError } from "@/nav/navSlice";
import { useBid } from "@/sheet/bidHooks";
import { BID_TYPES, BID_ACTIVITY_SIGNED } from "@/sheet/constants";
import { TopSheetPreview } from "@/sheet/TopSheetPreview";
import { formattedCurrency } from "@/utils";

export default function ProjectBids({
    bids,
    setBids,
    projectCurrency,
    confidentialDataPermission,
    updatePermission,
}) {
    const theme = useTheme();
    const mdBreakpoint = useMediaQuery(theme.breakpoints.down("md"));
    const smBreakpoint = useMediaQuery(theme.breakpoints.down("sm"));

    const [topsheetBidId, setTopsheetBidId] = useState(null);
    const getBid = useBid();
    const dispatch = useDispatch();

    const handleBidType = (bidId) => (event) => {
        const bidType = event.target.value;
        setBids(
            bids.map((bid) =>
                bid.id == bidId ? { ...bid, bid_type: bidType } : bid,
            ),
        );
        request.patch(`/bids/${bidId}`, { bid_type: bidType });
    };

    const handleBidDeleteConfirm = (bidId) => {
        request
            .delete(`/bids/${bidId}`)
            .then((response) => {
                setBids([...response.data]);
            })
            .catch((err) => {
                if (err.response.status == 400) {
                    dispatch(setError({ message: err.response.data.detail }));
                }
            });
    };

    // TODO: create and move to a FilesUploaderForBidEstimates component
    const handleBidFileUploaded = (bidId, files) => {
        request
            .post(`/bids/${bidId}/activity`, {
                action: BID_ACTIVITY_SIGNED,
                note: "Confirm client signed by uploading existing signature",
            })
            .then((response) => {
                setBids(
                    [...bids].map((bid) => {
                        return bid.id != bidId
                            ? bid
                            : {
                                  ...bid,
                                  status: response.data.bid_status,
                                  bid_type: response.data.bid_type,
                                  files: files,
                              };
                    }),
                );
            });
    };

    useEffect(() => {
        if (topsheetBidId) {
            getBid(topsheetBidId);
        }
    }, [topsheetBidId]);

    return (
        <>
            <Modal
                open={topsheetBidId}
                onClose={() => {
                    setTopsheetBidId(null);
                }}
            >
                <div style={{ display: "flex", height: "100%" }}>
                    <Fab
                        size="small"
                        sx={{ position: "absolute", top: 16, right: 16 }}
                        onClick={() => setTopsheetBidId(null)}
                    >
                        <CloseIcon />
                    </Fab>

                    <TopSheetPreview viewOnly />
                </div>
            </Modal>
            <TableContainer sx={{ paddingBottom: 5 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            {!mdBreakpoint && (
                                <>
                                    <TableCell align="right">Quoted</TableCell>
                                    {confidentialDataPermission ? (
                                        <TableCell align="right">GP</TableCell>
                                    ) : null}
                                </>
                            )}
                            {!smBreakpoint && (
                                <>
                                    <TableCell align="right"></TableCell>
                                    <TableCell align="right">Status</TableCell>
                                </>
                            )}
                            {updatePermission || !confidentialDataPermission ? (
                                <TableCell align="right"></TableCell>
                            ) : null}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bids
                            ? bids.map((bid) => {
                                  return (
                                      <TableRow
                                          key={bid.id}
                                          data-testid="bid-row"
                                      >
                                          <TableCell>
                                              {updatePermission ? (
                                                  <InternalLink
                                                      href={`/bids/${bid.id}`}
                                                      sx={{
                                                          textDecoration:
                                                              "none",
                                                          "&:hover": {
                                                              textDecoration:
                                                                  "underline",
                                                          },
                                                      }}
                                                  >
                                                      {bid.name}
                                                  </InternalLink>
                                              ) : (
                                                  bid.name
                                              )}
                                          </TableCell>
                                          {!mdBreakpoint && (
                                              <>
                                                  <TableCell align="right">
                                                      {formattedCurrency(
                                                          bid.client_quoted,
                                                          projectCurrency,
                                                          true,
                                                      )}
                                                  </TableCell>
                                                  {confidentialDataPermission ? (
                                                      <TableCell align="right">
                                                          {formattedCurrency(
                                                              bid.gross_profit,
                                                              projectCurrency,
                                                          )}
                                                      </TableCell>
                                                  ) : null}
                                              </>
                                          )}
                                          {!smBreakpoint && (
                                              <>
                                                  <TableCell align="right">
                                                      {updatePermission ? (
                                                          <Select
                                                              value={
                                                                  bid.bid_type
                                                              }
                                                              onChange={handleBidType(
                                                                  bid.id,
                                                              )}
                                                              input={
                                                                  <Input
                                                                      disableUnderline
                                                                  />
                                                              }
                                                              renderValue={(
                                                                  value,
                                                              ) => (
                                                                  <Chip
                                                                      label={
                                                                          value
                                                                      }
                                                                  />
                                                              )}
                                                          >
                                                              {BID_TYPES.map(
                                                                  (type, i) => (
                                                                      <MenuItem
                                                                          key={
                                                                              i
                                                                          }
                                                                          value={
                                                                              type
                                                                          }
                                                                      >
                                                                          {type}
                                                                      </MenuItem>
                                                                  ),
                                                              )}
                                                          </Select>
                                                      ) : (
                                                          <Chip
                                                              label={
                                                                  bid.bid_type
                                                              }
                                                          />
                                                      )}
                                                  </TableCell>
                                                  <TableCell align="right">
                                                      {bid.status}
                                                  </TableCell>
                                              </>
                                          )}
                                          <TableCell align="right">
                                              <Grid
                                                  container
                                                  justifyContent={"end"}
                                                  flexWrap="nowrap"
                                              >
                                                  <BidRowButtons
                                                      bid={bid}
                                                      handleBidFileUploaded={
                                                          handleBidFileUploaded
                                                      }
                                                      openTopsheet={() => {
                                                          setTopsheetBidId(
                                                              bid.id,
                                                          );
                                                      }}
                                                      handleDelete={() =>
                                                          handleBidDeleteConfirm(
                                                              bid.id,
                                                          )
                                                      }
                                                      readOnly={
                                                          !updatePermission
                                                      }
                                                      setTopsheetBidId={
                                                          setTopsheetBidId
                                                      }
                                                  />
                                              </Grid>
                                          </TableCell>
                                      </TableRow>
                                  );
                              })
                            : null}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}
